// pages/PimsPage.tsx

import React, { useEffect, useState } from 'react';
import { Card, Button, Container, Row, Col, ToastContainer, Toast, Modal, Form, Spinner } from 'react-bootstrap';
import {
  fetchSupportedPims,
  fetchPimsConfig,
  fetchPimsFields,
  savePimsConfig,
} from '../molecules/api';
import { PimsStatus, PimsSchema, PimsConfig } from '../../types/pims';
import { PimsConfigModal } from '../organisms/PimsConfigModal';
import { fetchWithJwt } from '../../utils/fetchWithJwt';

export const PimsPage: React.FC = () => {
  const [pimsList, setPimsList] = useState<PimsStatus[]>([]);
  const [selected, setSelected] = useState<PimsStatus | null>(null);
  const [schema, setSchema] = useState<PimsSchema | null>(null);
  const [initialValues, setInitialValues] = useState<PimsConfig | undefined>(undefined);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availablePims, setAvailablePims] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    fetchSupportedPims().then(setPimsList);
  }, []); // Only run once on mount

  useEffect(() => {
    // Get list of available PIMS that aren't already configured
    setAvailablePims(['Covetrus', 'Shepherd', 'Vetspire', 'Digitail']
      .filter(name => !pimsList.some(p => p.name === name)));
  }, [pimsList]);

  const handleAddPimsSave = async (pimsName: string) => {
    const pimsId = pimsName.toLowerCase();
    const schema = await fetchPimsFields(pimsId);
    setSchema(schema);
    setInitialValues({});
    setSelected({ id: pimsId, name: pimsName, isEnabled: false, pimsType: pimsId });
    setShowAddModal(false);
  };

  const handleClick = async (pims: PimsStatus) => {
    const schema = await fetchPimsFields(pims.pimsType);
    console.log('Schema from API:', schema);
    console.log('Fields from schema:', schema.fields);
    setSchema(schema);

    if (pims.isEnabled || pims.id) {
      const config = await fetchPimsConfig(`${pims.id}`);
      setInitialValues(config ?? {});
    } else {
      setInitialValues({});
    }

    setSelected(pims);
  };

  const handleSave = async (config: PimsConfig) => {
    if (!selected) return;
    if (!selected.isEnabled) {
      // For new PIMS, use POST without ID in path
      await fetchWithJwt('/pims-configuration', {
        method: "POST",
        body: JSON.stringify({
          integrationType: selected.pimsType.charAt(0).toUpperCase() + selected.pimsType.slice(1),
          config,
          pimsEnabled: true,
        }),
      });
    } else {
      // For existing PIMS, use PUT with the database ID
      await fetchWithJwt(`/pims-configuration/${String(selected.id)}`, {
        method: "PUT",
        body: JSON.stringify({
          config,
          pimsEnabled: true,
        }),
      });
    }
    setToastMsg(`${selected.name} saved successfully`);
    const updated = await fetchSupportedPims();
    setPimsList(updated);
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await fetchWithJwt(`/pims-import`, {
        method: 'POST'
      });
      setToastMsg('Patient sync started successfully');
    } catch {
      setToastMsg('Failed to start patient sync');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Container className="my-4">
      <h3>Practice Information Management System Integration</h3>
      <Row>
        {pimsList.length === 0 && 
          <Spinner animation="border" role="status"></Spinner>
        }
        {pimsList.map(pims => (
          <Col key={String(pims.id)} md={6} lg={4} className="mb-4">
            <Card border={pims.isEnabled ? 'success' : 'secondary'}>
              <Card.Body>
                <Card.Title>{pims.name}</Card.Title>
                <Card.Text>
                  Status: {pims.isEnabled ? 'Enabled' : 'Not Configured'}
                </Card.Text>
                <div className="d-flex gap-2">
                  <Button onClick={() => handleClick(pims)}>
                    {pims.isEnabled ? 'Edit Configuration' : 'Enable'}
                  </Button>
                  {pims.isEnabled && (
                    <Button 
                      variant="outline-primary" 
                      onClick={handleSync}
                      disabled={isSyncing}
                    >
                      {isSyncing ? 'Syncing...' : 'Sync Patients'}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {showAddModal && (
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add New PIMS Integration</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Select PIMS Type</Form.Label>
                <Form.Select onChange={(e) => handleAddPimsSave(e.target.value)}>
                  <option value="">Select a PIMS...</option>
                  {availablePims.map(pims => (
                    <option key={pims} value={pims}>{pims}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {schema && selected && (
        <PimsConfigModal
          show={true}
          onClose={() => setSelected(null)}
          pimsId={selected.id ? String(selected.id) : selected.pimsType}
          pimsName={selected.name}
          fields={schema.fields}
          initialValues={initialValues}
          onSave={handleSave}
          isNew={!selected.isEnabled}
        />
      )}

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg="success"
          onClose={() => setToastMsg(null)}
          show={!!toastMsg}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};